<template>
  <div class="mx-auto max-w-[var(--container)] px-6 py-8">
    <Commerce-Breadcrumb :items="breadcrumbItems" />

    <template v-if="loadingCategories">
      <div class="mt-6 grid gap-4 sm:grid-cols-2">
        <CardSkeleton v-for="i in 8" :key="i" variant="service" />
      </div>
    </template>

    <template v-else-if="category">
      <h1 class="mt-4 text-2xl font-extrabold md:text-3xl">{{ category.name }}</h1>
      <p v-if="category.description" class="mt-2 text-muted-foreground">{{ category.description }}</p>

      <div v-if="loadingServices" class="mt-6 grid gap-4 sm:grid-cols-2">
        <CardSkeleton v-for="i in 8" :key="i" variant="service" />
      </div>
      <div v-else-if="services.length" class="mt-6">
        <div class="grid gap-4 sm:grid-cols-2">
          <ServiceCard v-for="s in services" :key="s.id" :service="s" />
        </div>

        <Pagination v-if="totalItems > 0" :current-page="currentPage" :items-per-page="itemsPerPage"
          :total-items="totalItems" appearance="commerce" @update:currentPage="goToPage" />
      </div>
      <div v-else class="mt-6">
        <Commerce-EmptyState glyph="⊘" title="سرویسی پیدا نشد" body="در این دسته‌بندی سرویسی ثبت نشده است.">
          <template #action>
            <Commerce-Button variant="outline" @click="goToAll">مشاهده همه خدمات</Commerce-Button>
          </template>
        </Commerce-EmptyState>
      </div>
    </template>

    <template v-else>
      <Commerce-EmptyState glyph="⊘" title="دسته‌بندی پیدا نشد" body="دسته‌بندی موردنظر وجود ندارد.">
        <template #action>
          <Commerce-Button variant="outline" @click="goToAll">مشاهده همه خدمات</Commerce-Button>
        </template>
      </Commerce-EmptyState>
    </template>
  </div>
</template>

<script setup lang="ts">
const siteUrl = useSiteUrl()

import { computed } from 'vue'
import { useSeoMeta } from '#app'

const router = useRouter()
const route = useRoute()

const slug = route.params.slug as string

const { categories, loadingCategories } = useCategories()

const category = computed(() => categories.value?.find((c) => c.slug === slug) ?? null)

const { services, pagination, loadingServices, setPage } = useServices({
  per_page: 16,
  filters: { category_slug: slug },
})

const currentPage = computed(() => pagination.value?.currentPage ?? 1)

const totalItems = computed(() => pagination.value?.total ?? 0)

const itemsPerPage = computed(() => pagination.value?.perPage ?? 15)

const breadcrumbItems = computed(() => [
  { label: 'خانه', to: 'https://payjib.com' },
  { label: 'همهٔ خدمات', to: '/' },
  { label: category.value?.name ?? 'دسته‌بندی' },
])

const goToPage = (page: number) => {
  setPage(page)
}

const goToAll = () => {
  router.push('/')
}

useSeoMeta({
  title: () => (category.value ? `${category.value.name} - پی‌جیب` : 'دسته‌بندی خدمات - پی‌جیب'),

  description: () => category.value?.description || 'خرید و شارژ اشتراک سرویس‌های بین‌المللی.',

  robots: 'index,follow',

  ogTitle: () => (category.value ? `${category.value.name} - پی‌جیب` : 'دسته‌بندی خدمات - پی‌جیب'),

  ogDescription: () => category.value?.description || 'خرید و شارژ اشتراک سرویس‌های بین‌المللی.',

  ogImage: siteUrl('/services/payjib.webp'),
  ogUrl: () => siteUrl(`/services/category/${slug}/`),
  ogType: 'website',
  ogSiteName: 'پی‌جیب',
  ogLocale: 'fa_IR',

  twitterCard: 'summary_large_image',
  twitterTitle: () => (category.value ? `${category.value.name} - پی‌جیب` : 'دسته‌بندی خدمات - پی‌جیب'),
  twitterDescription: () => category.value?.description || 'خرید و شارژ اشتراک سرویس‌های بین‌المللی.',
  twitterImage: siteUrl('/services/payjib.webp'),
})
</script>
